/**
 * Journey Engine — composes a Guided Journey from structured data.
 *
 * Layers:
 *   1. base (classic) — slug, hero, steps[], trekDays[], faqs
 *   2. guidedMeta — timeline, budget, documentsWallet, packingChecklist, stepGuided
 *   3. deepContent — weather, emergency, offices, maps, travelerFaqs
 *
 * UI (GuidedJourneyPage) reads the merged object — no per-journey React code.
 */

export const JOURNEY_PRODUCT = {
	productName: 'Guided Journey',
	tagline: 'Always know the next step — complete each task with confidence.',
};

/** Standard 16-step lifecycle shared by trekking journeys */
export const JOURNEY_LIFECYCLE_STEP_IDS = [
	'dream', 'research', 'visa', 'flights', 'insurance', 'packing',
	'permits', 'permit-offices', 'guide', 'sherpa', 'porter',
	'kathmandu', 'lukla', 'trek', 'emergency', 'return',
];

/**
 * Merge base journey data with guided overlays and deep reference content.
 * @param {object} base — classic journey blueprint
 * @param {object} guidedMeta — preparation tools + stepGuided map
 * @param {object} deepContent — panels: weather, emergency, offices, etc.
 * @param {{ coverage?: number, status?: string }} [options]
 */
export function enrichJourneyWithGuided(base, guidedMeta, deepContent, options = {}) {
	const coverage = options.coverage ?? guidedMeta.coverage ?? base.coverage ?? 70;
	const status = options.status ?? base.status ?? 'complete';

	return {
		...base,
		coverage,
		status,
		productName: guidedMeta.productName || JOURNEY_PRODUCT.productName,
		tagline: guidedMeta.tagline || JOURNEY_PRODUCT.tagline,
		eyebrow: 'Guided Journey',
		preparationTimeline: guidedMeta.preparationTimeline,
		budget: guidedMeta.budget,
		documentsWallet: guidedMeta.documentsWallet,
		packingChecklist: guidedMeta.packingChecklist,
		...deepContent,
		trekDays: deepContent.trekDaysEnhanced || base.trekDays,
		faqs: base.faqs || [],
		travelerFaqs: deepContent.travelerFaqs || [],
		steps: base.steps.map((step) => ({
			...step,
			guided: {
				...(guidedMeta.stepGuided[step.id] || {}),
				officeLocations: step.id === 'permit-offices' ? deepContent.officeLocations : undefined,
			},
		})),
	};
}

/** Convenience alias for registry entries */
export function createJourney(base, guidedMeta, deepContent, options) {
	return enrichJourneyWithGuided(base, guidedMeta, deepContent, options);
}
